/// <amd-module name="WidgetCommon/ModernWidgetTypes/SettingsManagerBase" />
import { SemanticVersion } from "Dashboards/WidgetContracts/WidgetProps";
import * as Resources from "WidgetCommon/ModernWidgetTypes/Resources";

/** PARTIAL Description of  widget config - Does not include size property. This is a lowest-common-denominator interface between widget(pixel-based size) and widget config props (grid cell-based size).  */
export interface WidgetSettingsManagerProps {
    /** Fully qualified Identifier of Widget Contribution. This can be helpful for scenarios such as telemetry, on codepaths shared by different widgets. */
    contributionId: string;

    /** (Optional) Unique identifier of widget Configuration.
     *  Can be absent with widget previews during configuration, as well as widgets hosted on non-configurable "in-situ" experiences.
     */
    id?: string;

    /**
     * User-configurable name of widget. Rendered as title.
     */
    name: string;

    /**
     * Widget-defined Configuration. Supplied by Widget Configuration - default configuration is null.
     */
    settings: string | null;

    /**
     * Widget-defined settings version. Allows widget authors to recognize legacy data formats.
     */
    settingsVersion: SemanticVersion;
}

/** This is a re-usable contract for shared data generation on configurable widgets. */
export interface ISettingsManager<T> {
    /**
     * Provides initial settings, base on the last state.
     * If unconfigured, this will auto-populate smart defaults. If settings are not correct, implementation will throw.
     *
     * returnIncompleteSettings bypassses validation for generated settings, to allow user to see problems with a config in the UI.
     */
    ensureInitialSettings(widgetProps: WidgetSettingsManagerProps, returnIncompleteSettings?: boolean): Promise<T>;

    /** Signifies if the widget is configured. If not, we're operating with defaults. Note: A stored configuration may be entirely composed of defaults. */
    isCorrectlyConfigured(widgetProps: WidgetSettingsManagerProps): boolean;
}

export abstract class SettingsManagerBase<T> implements ISettingsManager<T> {
    public constructor() {}

    public isCorrectlyConfigured(widgetProps: WidgetSettingsManagerProps): boolean {
        return this.isConfigured(widgetProps);
    }
    /** Returns supplied settings if valid, otherwise attempts to generate settings.
     * Note: The input and output contracts need to be normalized.
     */
    public async ensureInitialSettings(widgetProps: WidgetSettingsManagerProps, returnIncompleteSettings?: boolean): Promise<T> {
        let initialSettings: T;
        if (this.isConfigured(widgetProps)) {
            initialSettings = widgetProps.settings ? JSON.parse(widgetProps.settings) : undefined;
            if (!returnIncompleteSettings) {
                this.evaluateSettingsForValidity(widgetProps, initialSettings, Resources.UnableToValidateSettingsMessage);
            }
            return Promise.resolve(initialSettings);
        } else {
            const defaultSettings = await this.generateDefaultSettings();
            if (!returnIncompleteSettings) {
                this.evaluateSettingsForValidity(widgetProps, defaultSettings, Resources.UnableToGenerateSettingsMessage);
            }
            return defaultSettings;
        }
    }

    public abstract getFeatureName(): string;

    public abstract generateDefaultSettings(): Promise<T>;

    public isConfigured(widgetProps: WidgetSettingsManagerProps): boolean {
        /*
         * If there's *anything* in data, we don't have a default configuration.
         * We consider null the same as undefined: Unconfigured.
         */
        return widgetProps.settings != null;
    }

    private evaluateSettingsForValidity(widgetProps: WidgetSettingsManagerProps, settings: T, validationError: string) {
        const testProps = { ...widgetProps };
        testProps.settings = JSON.stringify(settings);
        if (!this.isCorrectlyConfigured(testProps)) {
            throw new Error(validationError);
        }
    }
}
